import { useDebounce } from '@/hooks/useDebounce';
import { PaymentType } from '@/models/Payment';
import { TreeLineReceiptDetails } from '@/models/TreeLineReceiptDetails';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  TreeLineBillingFieldConfig,
  TreeLineManualBillingAddress,
  TreeLinePreviewFieldConfig,
  TreeLinePreviewFieldKey,
} from './types';
import { isEmail } from '@/utils/emailUtil';
import { logError } from '@/utils/errorHandlerUtil';

type Params = {
  isActive: boolean;
  defaultLocale: string;
  getTreeLineReceiptDetails: (
    applicationId: number,
    localeCode?: string
  ) => Promise<TreeLineReceiptDetails>;
  onLoadError: () => void;
};

const createTreeLineFieldOverrides = (): Record<TreeLinePreviewFieldKey, boolean> => ({
  email: false,
  year: false,
  utilityName: false,
  paymentType: false,
  amountPaid: false,
  paymentDate: false,
  transactionId: false,
  customerId: false,
  billingFullName: false,
});

const treeLinePreviewFields: TreeLinePreviewFieldConfig[] = [
  { key: 'email', label: 'Email', required: true },
  { key: 'year', label: 'Year', type: 'number', required: true },
  { key: 'utilityName', label: 'Utility Name', required: true },
  { key: 'paymentType', label: 'Payment Type', required: true },
  { key: 'transactionId', label: 'Card Connect Transaction Id', required: false },
  { key: 'customerId', label: 'Customer Id', required: false },
  { key: 'amountPaid', label: 'Amount Paid', required: true },
  { key: 'paymentDate', label: 'Payment Date', required: true },
];

const createEmptyTreeLineManualBillingAddress = (): TreeLineManualBillingAddress => ({
  billingFullName: '',
  billingAddress1: '',
  billingAddress2: '',
  billingCity: '',
  billingStateCode: '',
  billingPostalCode: '',
});

const treeLineBillingFields: TreeLineBillingFieldConfig[] = [
  { key: 'billingFullName', label: 'Billing Full Name', required: true },
  { key: 'billingAddress1', label: 'Billing Address 1', required: true },
  { key: 'billingAddress2', label: 'Billing Address 2', required: false },
  { key: 'billingCity', label: 'Billing City', required: true },
  { key: 'billingStateCode', label: 'Billing State Code', required: true },
  { key: 'billingPostalCode', label: 'Billing Postal Code', required: true },
];

const createEmptyTreeLineReceiptPreview = (defaultLocale: string): TreeLineReceiptDetails => ({
  email: '',
  year: -1,
  utilityName: '',
  paymentType: PaymentType.Unknown,
  amountPaid: -1,
  paymentDate: '',
  billingFullName: '',
  billingAddress1: '',
  billingAddress2: '',
  billingCity: '',
  billingStateCode: '',
  billingPostalCode: '',
  transactionId: '',
  customerId: '',
  localeCode: defaultLocale,
});

const normalizePaymentType = (value: unknown): PaymentType => {
  if (value == null) {
    return PaymentType.Unknown;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    if (value === Number(PaymentType.CreditCard)) {
      return PaymentType.CreditCard;
    }

    if (value === Number(PaymentType.OnAccount)) {
      return PaymentType.OnAccount;
    }

    return PaymentType.Unknown;
  }

  if (typeof value !== 'string') {
    return PaymentType.Unknown;
  }

  const normalized = value.trim().toLowerCase();
  if (normalized === '') {
    return PaymentType.Unknown;
  }

  const numericValue = Number(normalized);
  if (Number.isFinite(numericValue)) {
    if (numericValue === Number(PaymentType.CreditCard)) {
      return PaymentType.CreditCard;
    }

    if (numericValue === Number(PaymentType.OnAccount)) {
      return PaymentType.OnAccount;
    }

    return PaymentType.Unknown;
  }

  if (normalized === PaymentType[PaymentType.CreditCard].toLowerCase()) {
    return PaymentType.CreditCard;
  }

  if (normalized === PaymentType[PaymentType.OnAccount].toLowerCase()) {
    return PaymentType.OnAccount;
  }

  return PaymentType.Unknown;
};

export default function useTreeLineReceiptFormState({
  isActive,
  defaultLocale,
  getTreeLineReceiptDetails,
  onLoadError,
}: Params) {
  const [treeLineReceiptApplicationId, setTreeLineReceiptApplicationId] = useState<number>(0);
  const [treeLineReceiptPreview, setTreeLineReceiptPreview] = useState<TreeLineReceiptDetails>(
    createEmptyTreeLineReceiptPreview(defaultLocale)
  );
  const [treeLineReceiptLocaleCode, setTreeLineReceiptLocaleCode] = useState<string>(defaultLocale);
  const [treeLineManualBillingAddress, setTreeLineManualBillingAddress] =
    useState<TreeLineManualBillingAddress>(createEmptyTreeLineManualBillingAddress());
  const [treeLineFieldOverrides, setTreeLineFieldOverrides] = useState<
    Record<TreeLinePreviewFieldKey, boolean>
  >(createTreeLineFieldOverrides());
  const [loadingTreeLineReceiptPreview, setLoadingTreeLineReceiptPreview] =
    useState<boolean>(false);

  const debouncedTreeLineReceiptApplicationId = useDebounce(treeLineReceiptApplicationId, 500);

  const resetTreeLineReceiptForm = useCallback(() => {
    setTreeLineReceiptApplicationId(0);
    setTreeLineReceiptLocaleCode(defaultLocale);
    setTreeLineReceiptPreview(createEmptyTreeLineReceiptPreview(defaultLocale));
    setTreeLineManualBillingAddress(createEmptyTreeLineManualBillingAddress());
    setTreeLineFieldOverrides(createTreeLineFieldOverrides());
  }, [defaultLocale]);

  const toggleTreeLineFieldOverride = (field: TreeLinePreviewFieldKey) => {
    setTreeLineFieldOverrides((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const updateTreeLinePreviewField = (field: TreeLinePreviewFieldKey, value: string) => {
    if (field === 'year') {
      setTreeLineReceiptPreview((prev) => ({
        ...prev,
        year: value.trim() === '' ? 0 : Number(value),
      }));
      return;
    }

    if (field === 'paymentType') {
      setTreeLineReceiptPreview((prev) => ({
        ...prev,
        paymentType: normalizePaymentType(value),
      }));
      return;
    }

    setTreeLineReceiptPreview((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const isTreeLineReceiptValid = useMemo(() => {
    const hasMissingRequiredBillingField = treeLineBillingFields.some(
      (fieldConfig: TreeLineBillingFieldConfig) => {
        if (fieldConfig.required !== true) {
          return false;
        }

        const fieldKey: keyof TreeLineManualBillingAddress = fieldConfig.key;
        return treeLineManualBillingAddress[fieldKey].trim() === '';
      }
    );

    const isCreditCardPayment = treeLineReceiptPreview.paymentType === PaymentType.CreditCard;
    const hasRequiredTransactionId =
      !isCreditCardPayment || treeLineReceiptPreview.transactionId.trim() !== '';

    const isOnAccountPayment = treeLineReceiptPreview.paymentType === PaymentType.OnAccount;
    const hasRequiredCustomerId =
      !isOnAccountPayment || (treeLineReceiptPreview.customerId ?? '').trim() !== '';

    return (
      treeLineReceiptApplicationId > 0 &&
      isEmail(treeLineReceiptPreview.email) &&
      treeLineReceiptPreview.paymentType !== PaymentType.Unknown &&
      hasRequiredTransactionId &&
      hasRequiredCustomerId &&
      treeLineReceiptLocaleCode.trim() !== '' &&
      !hasMissingRequiredBillingField
    );
  }, [
    treeLineManualBillingAddress,
    treeLineReceiptApplicationId,
    treeLineReceiptLocaleCode,
    treeLineReceiptPreview.email,
    treeLineReceiptPreview.transactionId,
    treeLineReceiptPreview.customerId,
    treeLineReceiptPreview.paymentType,
  ]);

  const resolvedTreeLinePreviewFields = useMemo(() => {
    const isCreditCardPayment = treeLineReceiptPreview.paymentType === PaymentType.CreditCard;
    const isOnAccountPayment = treeLineReceiptPreview.paymentType === PaymentType.OnAccount;

    return treeLinePreviewFields.map((fieldConfig) => {
      if (fieldConfig.key === 'transactionId') {
        return { ...fieldConfig, required: isCreditCardPayment };
      }

      if (fieldConfig.key === 'customerId') {
        return { ...fieldConfig, required: isOnAccountPayment };
      }

      return fieldConfig;
    });
  }, [treeLineReceiptPreview.paymentType]);

  useEffect(() => {
    const loadTreeLineReceiptPreview = async () => {
      if (!isActive || debouncedTreeLineReceiptApplicationId <= 0) {
        setTreeLineReceiptPreview(createEmptyTreeLineReceiptPreview(defaultLocale));
        setTreeLineManualBillingAddress(createEmptyTreeLineManualBillingAddress());
        setTreeLineFieldOverrides(createTreeLineFieldOverrides());
        return;
      }

      try {
        setLoadingTreeLineReceiptPreview(true);
        const applicationData: TreeLineReceiptDetails = await getTreeLineReceiptDetails(
          debouncedTreeLineReceiptApplicationId,
          treeLineReceiptLocaleCode
        );

        const yearSource = applicationData.year;
        const yearFromApplicationCycle = Number(yearSource);

        const utilityName = applicationData.utilityName;
        const transactionId = applicationData.transactionId;
        const customerId = applicationData.customerId ?? '';

        setTreeLineReceiptPreview({
          email: applicationData.email,
          year: Number.isNaN(yearFromApplicationCycle) ? -1 : yearFromApplicationCycle,
          utilityName,
          paymentType: normalizePaymentType(applicationData.paymentType),
          amountPaid: applicationData.amountPaid,
          paymentDate: applicationData.paymentDate,
          transactionId,
          customerId,
          localeCode: applicationData.localeCode ?? defaultLocale,
        });
        setTreeLineFieldOverrides(createTreeLineFieldOverrides());
      } catch (error) {
        logError(`Error loading TreeLine receipt email form state.`, error);

        setTreeLineReceiptPreview(createEmptyTreeLineReceiptPreview(defaultLocale));
        setTreeLineFieldOverrides(createTreeLineFieldOverrides());
        onLoadError();
      } finally {
        setLoadingTreeLineReceiptPreview(false);
      }
    };

    void loadTreeLineReceiptPreview();
  }, [
    debouncedTreeLineReceiptApplicationId,
    defaultLocale,
    getTreeLineReceiptDetails,
    isActive,
    onLoadError,
    treeLineReceiptLocaleCode,
  ]);

  return {
    treeLinePreviewFields: resolvedTreeLinePreviewFields,
    treeLineBillingFields,
    treeLineReceiptApplicationId,
    setTreeLineReceiptApplicationId,
    treeLineReceiptPreview,
    treeLineReceiptLocaleCode,
    setTreeLineReceiptLocaleCode,
    treeLineManualBillingAddress,
    setTreeLineManualBillingAddress,
    treeLineFieldOverrides,
    loadingTreeLineReceiptPreview,
    toggleTreeLineFieldOverride,
    updateTreeLinePreviewField,
    isTreeLineReceiptValid,
    resetTreeLineReceiptForm,
  };
}
